/*
 * The git surface — a review surface, deliberately not a git client.
 *
 * Three things and no more: a commit timeline grouped by operation, the diff of
 * whatever is selected, and revert. No branch graph, no staging area, no merge UI —
 * those are git concepts nobody asked for, and offering them would suggest this is
 * a place to do version control rather than a place to check what the agent wrote.
 *
 * What the API does and does not give shapes two things here, and both are stated
 * on screen rather than papered over:
 *
 *  - revert is per OPERATION. There is no per-commit revert endpoint, so a commit
 *    inside a multi-commit operation offers the operation's revert and says why.
 *  - per-page history and blame are DERIVED by scanning operation diffs, because no
 *    endpoint answers either. The scan is bounded and its extent is reported, so the
 *    answer reads as "over the N operations scanned".
 *
 * There is a third case this tab must not smooth over. `git_head` is null both when
 * a base has no history yet and when its repository reached the pod without a
 * `.git` — and if operations exist while no commit does, the second is what
 * happened. Rendered as an ordinary empty timeline that reads as "a quiet base",
 * which is the most expensive kind of wrong: revert is the entire safety story here,
 * and revert with no commits behind it has nothing to act on. So the two are told
 * apart and the second says so loudly.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  getOperationDiff,
  gitBundleUrl,
  revertOperation,
  type History,
  type HistoryOperation,
  type OperationDiff,
} from '../../../api/knowledgeBases.ts'
import { Empty, Loaded } from '../components/AsyncState.tsx'
import { DiffView, type DiffMode } from '../components/DiffView.tsx'
import { FrontmatterTable } from '../components/FrontmatterTable.tsx'
import { countLines } from '../components/IngestCard.tsx'
import { byteDelta, dateTime, num, shortSha, shortStamp } from '../lib/format.ts'
import { authorBadge, authorLabel, isRunning, opLook, statusLook } from '../lib/operations.ts'
import { SCAN_BATCH, useDiffScan } from '../lib/useDiffScan.ts'
import { useResource, type Resource } from '../lib/useResource.ts'

interface HistoryTabProps {
  kbId: string
  history: Resource<History>
  gitHead: string | null
  onChanged: () => void
}

export function HistoryTab({ kbId, history, gitHead, onChanged }: HistoryTabProps) {
  const [selectedOpId, setSelectedOpId] = useState<string | null>(null)
  const [mode, setMode] = useState<DiffMode>('unified')
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [reverting, setReverting] = useState<string | null>(null)
  const [revertError, setRevertError] = useState<string | null>(null)
  const [pane, setPane] = useState<'diff' | 'page' | 'blame'>('diff')

  const ops = history.data?.ops ?? []

  useEffect(() => {
    if (selectedOpId === null && ops[0]) setSelectedOpId(ops[0].op_id)
  }, [ops, selectedOpId])

  const selectedOp = ops.find((op) => op.op_id === selectedOpId) ?? null

  const diff = useResource<OperationDiff>(`${kbId}:diff:${selectedOpId ?? 'none'}`, () =>
    selectedOpId
      ? getOperationDiff(kbId, selectedOpId)
      : Promise.resolve<OperationDiff>({ files: [] }),
  )

  const files = diff.data?.files ?? []
  const activePath = selectedPath && files.some((f) => f.path === selectedPath)
    ? selectedPath
    : (files[0]?.path ?? null)
  const activeFile = files.find((file) => file.path === activePath) ?? null

  const scan = useDiffScan(kbId, history.data)

  // Operations recorded, no commit anywhere: the history did not reach the base.
  const commitsMissing = ops.length > 0 && ops.every((op) => !op.git_commit_first)

  async function revert(op: HistoryOperation) {
    setReverting(op.op_id)
    setRevertError(null)
    try {
      await revertOperation(kbId, op.op_id)
      onChanged()
      history.reload()
    } catch (err) {
      setRevertError(err instanceof Error ? err.message : 'откат не выполнен')
    } finally {
      setReverting(null)
    }
  }

  return (
    <div className="pane">
      <div className="callout" style={{ marginBottom: 16 }}>
        <b>wiki — это настоящий локальный git-репозиторий.</b> Каждый чекпоинт операции — коммит,
        вся операция — диапазон коммитов. Именно поэтому агент работает{' '}
        <b>без approve-гейтов</b>: путь восстановления — revert, а не «подтвердите заранее». История
        не перезаписывается: откат — это новый коммит.
      </div>

      {commitsMissing ? (
        <div className="callout callout--danger" style={{ marginBottom: 14 }}>
          <b>Операции есть, а коммитов нет — история не доехала до базы.</b> Это не «тихая база»:{' '}
          {num(ops.length)} операций записано, но ни одна не назвала коммит
          {gitHead ? '' : ', и HEAD пуст'}. Откатывать нечего, потому что откат — это обратный
          коммит поверх существующей истории. Стоит сообщить об этом оператору, а не считать, что
          агент ничего не писал.
        </div>
      ) : null}

      {revertError ? (
        <div className="callout callout--danger" style={{ marginBottom: 14 }}>
          <b>Откат не выполнен.</b> {revertError}
        </div>
      ) : null}

      <div className="hist">
        <div className="card">
          <div className="card__head">
            <h3>
              Коммиты{' '}
              <span className="path">
                HEAD →{' '}
                {gitHead ? (
                  shortSha(gitHead)
                ) : (
                  <span title="Пустой HEAD: либо истории ещё нет, либо она не доставлена">
                    пусто
                  </span>
                )}
              </span>
            </h3>
            <div className="right">
              <span className="pill pill--neutral pill--sm">
                {num(ops.length)} операций
              </span>
              <a
                className="btn btn--ghost btn--sm"
                href={gitBundleUrl(kbId)}
                download={`${kbId}.bundle`}
              >
                Скачать .git bundle
              </a>
            </div>
          </div>

          <Loaded resource={history} what="историю" skeletonRows={7}>
            {(data) =>
              (data.ops ?? []).length === 0 ? (
                <Empty title="Коммитов ещё нет">
                  История появится после первой операции: создание базы, ingest, lint или правка
                  схемы. Пустой список здесь означает именно это — ни одной операции ещё не было.
                </Empty>
              ) : (
                <div className="gitlog">
                  {(data.ops ?? []).map((op) => (
                    <OperationGroup
                      key={op.op_id}
                      op={op}
                      selected={op.op_id === selectedOpId}
                      reverting={reverting === op.op_id}
                      onSelect={() => {
                        setSelectedOpId(op.op_id)
                        setSelectedPath(null)
                        setPane('diff')
                      }}
                      onRevert={() => void revert(op)}
                    />
                  ))}
                </div>
              )
            }
          </Loaded>
        </div>

        <div className="card">
          <div className="card__head">
            <h3>
              {selectedOp ? (
                <>
                  <span className="mono" style={{ fontSize: 12 }}>
                    {shortSha(selectedOp.git_commit_last ?? selectedOp.git_commit_first)}
                  </span>{' '}
                  · {opLook(selectedOp.op).label}
                </>
              ) : (
                'Дифф'
              )}
            </h3>
            <div className="right">
              <div className="seg">
                <button
                  type="button"
                  aria-pressed={pane === 'diff'}
                  onClick={() => setPane('diff')}
                >
                  дифф
                </button>
                <button
                  type="button"
                  aria-pressed={pane === 'page'}
                  onClick={() => setPane('page')}
                >
                  история страницы
                </button>
                <button
                  type="button"
                  aria-pressed={pane === 'blame'}
                  onClick={() => setPane('blame')}
                >
                  blame
                </button>
              </div>
              {pane === 'diff' ? (
                <div className="seg">
                  <button
                    type="button"
                    aria-pressed={mode === 'unified'}
                    onClick={() => setMode('unified')}
                  >
                    unified
                  </button>
                  <button
                    type="button"
                    aria-pressed={mode === 'split'}
                    onClick={() => setMode('split')}
                  >
                    split
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {pane === 'diff' ? (
            selectedOpId === null ? (
              <Empty title="Выберите операцию слева" />
            ) : (
              <Loaded resource={diff} what="дифф операции" skeletonRows={6}>
                {(data) =>
                  (data.files ?? []).length === 0 ? (
                    <Empty title="Операция не изменила файлы">
                      Она могла быть только чтением — например query, который не подшивал ответ.
                    </Empty>
                  ) : (
                    <div className="card__body">
                      <div style={{ marginBottom: 13 }}>
                        {(data.files ?? []).map((file) => {
                          const counts = countLines(file)
                          return (
                            <button
                              key={file.path}
                              type="button"
                              className="dfile"
                              aria-current={file.path === activePath}
                              onClick={() => setSelectedPath(file.path)}
                            >
                              <span className="dfile__path">{file.path}</span>
                              <span className="dfile__delta">
                                +{counts.added} −{counts.removed}
                              </span>
                            </button>
                          )
                        })}
                      </div>

                      {activeFile ? (
                        <>
                          <span className="kicker" style={{ display: 'block', marginBottom: 8 }}>
                            Frontmatter
                          </span>
                          <FrontmatterTable changes={activeFile.frontmatter_changes ?? []} />

                          <span
                            className="kicker"
                            style={{ display: 'block', margin: '16px 0 8px' }}
                          >
                            Тело · +{countLines(activeFile).added} −
                            {countLines(activeFile).removed}
                          </span>
                          <DiffView hunks={activeFile.hunks ?? []} mode={mode} />

                          <p className="field__hint" style={{ marginTop: 11 }}>
                            Цитаты <code>^[файл:строки]</code> — lint перечитывает диапазон и
                            проверяет, что он всё ещё подтверждает утверждение.
                          </p>
                        </>
                      ) : null}
                    </div>
                  )
                }
              </Loaded>
            )
          ) : null}

          {pane === 'page' ? (
            <PageHistory
              scan={scan}
              path={activePath}
              paths={files.map((file) => file.path)}
              onPickPath={setSelectedPath}
              onPickOp={(opId) => {
                setSelectedOpId(opId)
                setPane('diff')
              }}
            />
          ) : null}

          {pane === 'blame' ? <Blame scan={scan} onPickOp={setSelectedOpId} /> : null}
        </div>
      </div>
    </div>
  )
}

function OperationGroup({
  op,
  selected,
  reverting,
  onSelect,
  onRevert,
}: {
  op: HistoryOperation
  selected: boolean
  reverting: boolean
  onSelect: () => void
  onRevert: () => void
}) {
  const look = opLook(op.op)
  const status = statusLook(op)
  const reverted = op.reverted_by_op_id !== null
  const commits = commitRange(op)

  return (
    <div className={`gop${reverted ? ' gop--reverted' : ''}`}>
      <div className="gop__head">
        <span className={`pill pill--sm ${look.pill}`}>{look.label}</span>
        <span className="gop__title">{op.title || op.op_id}</span>
        {reverted ? <span className="pill pill--neutral pill--sm">откачено</span> : null}
        {isRunning(op) ? <span className={`pill pill--sm ${status.pill}`}>идёт</span> : null}
        <span className="gop__meta">
          {commits} · {op.pages_created + op.pages_updated + op.pages_deleted} файл.
          {op.bytes_delta ? ` · ${byteDelta(op.bytes_delta)}` : ''}
        </span>
        {!reverted && !isRunning(op) && op.git_commit_first ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={reverting}
            onClick={onRevert}
          >
            {reverting ? 'Откатываем…' : 'Откатить операцию'}
          </button>
        ) : null}
      </div>

      <button type="button" className="gc" aria-current={selected} onClick={onSelect}>
        <span className="gc__sha">{shortSha(op.git_commit_last ?? op.git_commit_first)}</span>
        <span className="gc__who" title={authorLabel(op.author)}>
          {authorBadge(op.author)}
        </span>
        <span className="gc__subj">
          {op.op}: +{op.pages_created} новых, ~{op.pages_updated} обновлено
          {op.pages_deleted > 0 ? `, −${op.pages_deleted} удалено` : ''}
        </span>
        <span className="gc__delta">{status.label}</span>
        <span className="gc__time">{shortStamp(op.finished_at ?? op.started_at)}</span>
      </button>

      {op.git_commit_first && op.git_commit_last && op.git_commit_first !== op.git_commit_last ? (
        <p
          className="field__hint"
          style={{ padding: '2px 15px 4px 22px', margin: 0 }}
          title="Отдельного эндпоинта для откатa одного коммита нет"
        >
          Диапазон {shortSha(op.git_commit_first)}‥{shortSha(op.git_commit_last)} — откат делается
          целой операцией: отдельного отката одного коммита API не предлагает.
        </p>
      ) : null}
    </div>
  )
}

function commitRange(op: HistoryOperation): string {
  if (!op.git_commit_first) return 'без коммитов'
  if (!op.git_commit_last || op.git_commit_first === op.git_commit_last) return '1 коммит'
  return `${shortSha(op.git_commit_first)}‥${shortSha(op.git_commit_last)}`
}

function PageHistory({
  scan,
  path,
  paths,
  onPickPath,
  onPickOp,
}: {
  scan: ReturnType<typeof useDiffScan>
  path: string | null
  paths: string[]
  onPickPath: (path: string) => void
  onPickOp: (opId: string) => void
}) {
  const touches = useMemo(() => (path ? scan.touchesOf(path) : []), [path, scan])

  return (
    <div className="card__body">
      <div className="filters" style={{ marginBottom: 12 }}>
        <select
          className="input input--mono"
          value={path ?? ''}
          onChange={(event) => onPickPath(event.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        >
          {paths.length === 0 ? <option value="">нет страниц в этой операции</option> : null}
          {paths.map((candidate) => (
            <option key={candidate} value={candidate}>
              {candidate}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={scan.scanning}
          onClick={() => void scan.scan()}
        >
          {scan.scanning
            ? `Сканируем ${scan.progress?.done ?? 0}/${scan.progress?.total ?? 0}…`
            : 'Собрать историю страницы'}
        </button>
      </div>

      <div className="callout callout--info" style={{ marginBottom: 13 }}>
        Отдельного эндпоинта «история страницы» в API нет, поэтому она собирается на клиенте:
        просматриваются диффы последних {SCAN_BATCH} операций и отбираются те, где встречается эта
        страница. Ответ верен в пределах просканированного.
      </div>

      {scan.progress === null ? (
        <Empty title="История не собрана">Нажмите «Собрать историю страницы».</Empty>
      ) : touches.length === 0 ? (
        <Empty title="Эту страницу не трогали">
          Ни одна из просмотренных операций ({scan.progress.total}) не меняла {path}.
        </Empty>
      ) : (
        <div>
          {touches.map(({ op, file }) => {
            const counts = countLines(file)
            return (
              <button
                key={op.op_id}
                type="button"
                className="gc"
                style={{ paddingLeft: 11 }}
                onClick={() => onPickOp(op.op_id)}
              >
                <span className="gc__sha">
                  {shortSha(op.git_commit_last ?? op.git_commit_first)}
                </span>
                <span className="gc__who">{authorBadge(op.author)}</span>
                <span className="gc__subj">
                  {opLook(op.op).label}: {op.title || file.change}
                </span>
                <span className="gc__delta">
                  +{counts.added} −{counts.removed}
                </span>
                <span className="gc__time">{shortStamp(op.finished_at ?? op.started_at)}</span>
              </button>
            )
          })}
        </div>
      )}

      {scan.failures.length > 0 ? (
        <p className="field__hint" style={{ marginTop: 11 }}>
          Диффы {scan.failures.length} операций прочитать не удалось — в этой выборке их нет.
        </p>
      ) : null}
    </div>
  )
}

function Blame({
  scan,
  onPickOp,
}: {
  scan: ReturnType<typeof useDiffScan>
  onPickOp: (opId: string) => void
}) {
  const [fragment, setFragment] = useState('')
  const hit = useMemo(() => scan.blame(fragment), [fragment, scan])

  return (
    <div className="card__body">
      <div className="callout callout--info" style={{ marginBottom: 13 }}>
        <b>Blame здесь приблизительный, и это осознанно.</b> API не отдаёт построчную атрибуцию,
        поэтому ищется самая свежая операция, которая ДОБАВИЛА строку с этим фрагментом. Это отвечает
        на «откуда взялся этот абзац», но не на «кому он принадлежит сейчас».
      </div>

      <div className="filters" style={{ marginBottom: 12 }}>
        <input
          className="input"
          value={fragment}
          placeholder="Вставьте фрагмент абзаца…"
          onChange={(event) => setFragment(event.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={scan.scanning}
          onClick={() => void scan.scan()}
        >
          {scan.scanning
            ? `Сканируем ${scan.progress?.done ?? 0}/${scan.progress?.total ?? 0}…`
            : 'Просканировать диффы'}
        </button>
      </div>

      {scan.progress === null ? (
        <Empty title="Диффы не просканированы">
          Blame работает по загруженным диффам — нажмите «Просканировать диффы».
        </Empty>
      ) : fragment.trim().length < 3 ? (
        <Empty title="Введите фрагмент">Минимум три символа.</Empty>
      ) : hit === null ? (
        <Empty title="Ничего не найдено">
          Среди просмотренных {scan.progress.total} операций ни одна не добавляла строку с этим
          фрагментом.
        </Empty>
      ) : (
        <div>
          <div className="dfile" style={{ marginBottom: 10 }}>
            <span className="dfile__path">{hit.path}</span>
          </div>
          <div className="diff">
            <div className="diff__line diff__line--add">+ {hit.line}</div>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginTop: 11 }}
            onClick={() => onPickOp(hit.op.op_id)}
          >
            Открыть операцию {opLook(hit.op.op).label} · {dateTime(hit.op.started_at)}
          </button>
        </div>
      )}
    </div>
  )
}
